import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  UserIcon,
  CalendarDaysIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleLeftIcon,
  LinkIcon,
  PaperClipIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow } from 'date-fns';
import Button from '../common/Button';
import ConfirmationModal from '../common/ConfirmationModal';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Avatar from '../common/Avatar';
import { formatCurrency } from '../../utils/format';
import { useAppSelector } from '../../hooks/redux';
import ServicePackageDetailsModal from '../profile/ServicePackageDetailsModal';
import useBackButton from '../../hooks/useBackButton';

interface RequestDetailModalProps {
  request: any;
  onClose: () => void;
  onResponse: (requestId: string, status: 'accepted' | 'declined') => void;
  onWithdraw?: (requestId: string) => void;
  onComplete?: (request: any, alreadyConfirmed?: boolean) => void;
  onRevokeComplete?: (request: any) => void;
  onCancel?: (request: any) => void;
  onBook?: (request: any) => void;
  isReceived: boolean;
  zIndex?: number;
}

const RequestDetailModal: React.FC<RequestDetailModalProps> = ({
  request,
  onClose,
  onResponse,
  onWithdraw,
  onComplete,
  onRevokeComplete,
  onCancel,
  onBook,
  isReceived,
  zIndex
}) => {
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [responseMessage, setResponseMessage] = useState('');
  const [showResponseField, setShowResponseField] = useState(false);
  const [responding, setResponding] = useState(false);
  const [showPackageDetails, setShowPackageDetails] = useState(false);
  const [showConfirmComplete, setShowCompleteConfirm] = useState(false);
  const navigate = useNavigate();

  // Handle Android back button / back swipe gesture
  useBackButton(
    () => {
      if (showPackageDetails) {
        setShowPackageDetails(false);
        return true;
      }
      if (showConfirmComplete) {
        setShowCompleteConfirm(false);
        return true;
      }
      onClose();
      return true;
    },
    true,
    100
  );

  const handleResponse = async (status: 'accepted' | 'declined') => {
    setResponding(true);
    try {
      await onResponse(request.id, status);
      onClose();
    } catch (error) {
      console.error('Failed to respond:', error);
    } finally {
      setResponding(false);
    }
  };

  const handleMessageApplicant = (applicant: any) => {
    navigate('/messages', {
      state: {
        openConversationWith: applicant.id,
        collaboration_request_id: request.id,
        userName: `${applicant.first_name} ${applicant.last_name}`,
        userAvatar: applicant.profile_pic_signed,
        userType: applicant.user_type,
        prefilledMessage: "hello"
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white border-transparent shadow-sm shadow-yellow-500/20';
      case 'accepted': return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white border-transparent shadow-sm shadow-green-500/20';
      case 'declined': return 'bg-gradient-to-r from-red-500 to-rose-500 text-white border-transparent shadow-sm shadow-red-500/20';
      case 'completed': return 'bg-gradient-to-r from-orange-500 to-amber-500 text-white border-transparent shadow-sm shadow-orange-500/20';
      case 'cancelled': return 'bg-gradient-to-r from-slate-500 to-gray-500 text-white border-transparent shadow-sm';
      case 'expired': return 'bg-gradient-to-r from-gray-400 to-slate-400 text-white border-transparent shadow-sm';
      default: return 'bg-gradient-to-r from-gray-500 to-slate-500 text-white border-transparent';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'urgent': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800/50';
      case 'high': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800/50';
      case 'normal': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800/50';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700';
    }
  };

  const isExpiring = () => {
    if (!request.expires_at) return false;
    const expirationDate = new Date(request.expires_at);
    const now = new Date();
    const hoursUntilExpiration = (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilExpiration <= 24 && hoursUntilExpiration > 0;
  };

  const getHeaderStyles = (status: string) => {
    switch (status) {
      case 'pending': return {
        bg: 'bg-gradient-to-r from-amber-600 to-yellow-500 shadow-md shadow-amber-500/10',
        text: 'text-white',
        subtext: 'text-amber-100',
        closeIcon: 'text-white/80 hover:bg-white/10'
      };
      case 'accepted': return {
        bg: 'bg-gradient-to-r from-emerald-600 to-teal-500 shadow-md shadow-emerald-500/10',
        text: 'text-white',
        subtext: 'text-emerald-100',
        closeIcon: 'text-white/80 hover:bg-white/10'
      };
      case 'declined': return {
        bg: 'bg-gradient-to-r from-rose-600 to-red-500 shadow-md shadow-rose-500/10',
        text: 'text-white',
        subtext: 'text-rose-100',
        closeIcon: 'text-white/80 hover:bg-white/10'
      };
      case 'completed': return {
        bg: 'bg-gradient-to-r from-orange-600 to-amber-500 shadow-md shadow-orange-500/10',
        text: 'text-white',
        subtext: 'text-orange-100',
        closeIcon: 'text-white/80 hover:bg-white/10'
      };
      case 'cancelled': return {
        bg: 'bg-gradient-to-r from-slate-700 to-slate-500 shadow-md',
        text: 'text-white',
        subtext: 'text-slate-200',
        closeIcon: 'text-white/80 hover:bg-white/10'
      };
      default: return {
        bg: 'bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800',
        text: 'text-gray-900 dark:text-white',
        subtext: 'text-gray-600 dark:text-gray-400',
        closeIcon: 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
      };
    }
  };

  const user = isReceived ? request.sender : request.receiver;

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 md:p-4"
      style={{ zIndex: zIndex || 10000 }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-900 w-full h-full md:w-full md:max-w-2xl md:h-auto md:max-h-[90vh] md:rounded-2xl shadow-xl flex flex-col overflow-hidden border border-gray-100 dark:border-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className={`p-6 ${getHeaderStyles(request.status).bg} transition-all`}
          style={{ paddingTop: 'calc(1.5rem + max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)))' }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative">
            <div 
              className="flex items-start space-x-3 cursor-pointer group pr-8 md:pr-0"
              onClick={() => user?.id && navigate(`/profile/${user.id}`)}
              title="View Profile"
            >
              <Avatar
                src={user?.profile_pic_signed || user?.profile_pic}
                alt={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User'}
                size="lg"
                className="ring-2 ring-white/30 shadow-sm group-hover:scale-105 transition-transform shrink-0"
              />
              <div className="min-w-0 flex-1">
                <h2 className={`text-lg md:text-xl font-black leading-tight ${getHeaderStyles(request.status).text} group-hover:underline underline-offset-2 break-words`}>
                  {isReceived ? 'Request from' : 'Request to'} {user?.first_name} {user?.last_name}
                </h2>
                <div className={`text-xs md:text-sm mt-1 ${getHeaderStyles(request.status).subtext} font-medium capitalize flex flex-wrap items-center gap-1.5`}>
                  <span>{request.type} collaboration</span>
                  {request.service_package_id && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-100/90 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 shadow-sm">
                      📦 Package Booking
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between md:justify-end gap-3 mt-1 md:mt-0 shrink-0">
              <span className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-sm border ${request.status !== 'default' ? 'bg-white/20 text-white border-white/20 shadow-sm' : getStatusColor(request.status)}`}>
                {request.status === 'pending' && <ClockIcon className="h-3.5 w-3.5 mr-1" />}
                {request.status === 'accepted' && <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />}
                {request.status === 'declined' && <XCircleIcon className="h-3.5 w-3.5 mr-1" />}
                {request.status === 'cancelled' && <XCircleIcon className="h-3.5 w-3.5 mr-1" />}
                {request.status === 'completed' && <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />}
                {request.status}
              </span>
              
              <button
                onClick={onClose}
                className={`absolute -top-1 -right-1 md:relative p-1.5 rounded-full transition-colors ${getHeaderStyles(request.status).closeIcon}`}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Subject & Message */}
          <div className="mb-6 bg-gradient-to-br from-indigo-50/30 via-white to-indigo-50/10 dark:from-indigo-900/10 dark:via-gray-800/20 dark:to-gray-900 rounded-2xl p-5 border border-indigo-50 dark:border-indigo-900/20 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 p-3 opacity-[0.03] dark:opacity-[0.05] pointer-events-none">
               <ChatBubbleLeftIcon className="w-24 h-24 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="mb-4">
              <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1">Subject</p>
              <h3 className="text-lg font-black text-gray-900 dark:text-white leading-snug">{request.subject}</h3>
            </div>
            <div className="pt-4 border-t border-indigo-100/50 dark:border-indigo-900/10">
              <p className="text-xs font-bold text-indigo-500 dark:text-indigo-400 uppercase tracking-wider mb-2">Message</p>
              <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap text-sm leading-relaxed font-medium bg-white/50 dark:bg-gray-800/30 p-3 rounded-lg border border-indigo-50/30 dark:border-indigo-900/10">{request.message}</p>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {/* Timeline */}
            <div className="flex items-center space-x-3 bg-gray-50/50 dark:bg-gray-800/20 p-3.5 rounded-xl border border-gray-100/50 dark:border-gray-800/50 shadow-sm">
              <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl text-indigo-600 dark:text-indigo-400">
                <CalendarDaysIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Timeline</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white capitalize">
                  {request.timeline?.replace('_', ' ') || 'Not specified'}
                </p>
              </div>
            </div>

            {/* Location */}
            {request.location && (
              <div className="flex items-center space-x-3 bg-gray-50/50 dark:bg-gray-800/20 p-3.5 rounded-xl border border-gray-100/50 dark:border-gray-800/50 shadow-sm">
                <div className="p-2.5 bg-rose-50 dark:bg-rose-900/30 rounded-xl text-rose-600 dark:text-rose-400">
                  <MapPinIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Location</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white truncate" title={request.location}>{request.location}</p>
                </div>
              </div>
            )}

            {/* Budget */}
            {(request.budget || request.is_paid !== undefined) && (
              <div className="flex items-center space-x-3 bg-gray-50/50 dark:bg-gray-800/20 p-3.5 rounded-xl border border-gray-100/50 dark:border-gray-800/50 shadow-sm">
                <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl text-emerald-600 dark:text-emerald-400">
                  <CurrencyDollarIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Budget</p>
                  <p className="text-sm font-black text-emerald-600 dark:text-emerald-400">
                    {request.is_paid 
                      ? formatCurrency(request.budget || 0)
                      : 'TFP (Portfolio)'}
                  </p>
                </div>
              </div>
            )}

            {/* Duration */}
            {request.duration && (
              <div className="flex items-center space-x-3 bg-gray-50/50 dark:bg-gray-800/20 p-3.5 rounded-xl border border-gray-100/50 dark:border-gray-800/50 shadow-sm">
                <div className="p-2.5 bg-cyan-50 dark:bg-cyan-900/30 rounded-xl text-cyan-600 dark:text-cyan-400">
                  <ClockIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Duration</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{request.duration}</p>
                </div>
              </div>
            )}

            {/* Created Date */}
            <div className="flex items-center space-x-3 bg-gray-50/50 dark:bg-gray-800/20 p-3.5 rounded-xl border border-gray-100/50 dark:border-gray-800/50 shadow-sm">
              <div className="p-2.5 bg-violet-50 dark:bg-violet-900/30 rounded-xl text-violet-600 dark:text-violet-400">
                <CalendarDaysIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Sent</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  {format(new Date(request.created_at), 'MMM d')} <span className="text-xs text-gray-400 font-normal">({formatDistanceToNow(new Date(request.created_at), { addSuffix: true })})</span>
                </p>
              </div>
            </div>
          </div>

          {/* Package Details Banner */}
          {request.service_package && (
            <div className="border border-purple-100 dark:border-purple-900/30 bg-purple-50/50 dark:bg-purple-900/10 rounded-xl p-4 mb-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-200 to-transparent dark:from-purple-800/40 rounded-bl-full opacity-50 pointer-events-none" />
              <div className="flex items-stretch gap-4 flex-col sm:flex-row">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mb-2">
                    <h4 className="text-sm font-black text-gray-900 dark:text-white flex items-center break-words">
                      📦 Booked Package: {request.service_package.title}
                    </h4>
                    <button 
                      onClick={() => setShowPackageDetails(true)}
                      className="text-xs font-bold text-purple-600 hover:text-purple-700 hover:underline dark:text-purple-400 dark:hover:text-purple-300 shrink-0 text-left sm:text-right"
                    >
                      View Details →
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{request.service_package.description}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {request.service_package.delivery_time_days > 0 && (
                      <span className="bg-white dark:bg-gray-800 px-2.5 py-1 rounded-md border border-gray-100 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-300">
                        Delivery: {request.service_package.delivery_time_days} Days
                      </span>
                    )}
                    {request.service_package.revisions > 0 && (
                      <span className="bg-white dark:bg-gray-800 px-2.5 py-1 rounded-md border border-gray-100 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-300">
                        Revisions: {request.service_package.revisions}
                      </span>
                    )}
                    {(request.service_package.number_of_edited_photos > 0) && (
                       <span className="bg-white dark:bg-gray-800 px-2.5 py-1 rounded-md border border-gray-100 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-300 flex items-center">
                         <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 mr-1" />
                         {request.service_package.number_of_edited_photos} Edits
                       </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-left sm:text-right mt-4 sm:mt-0 pt-4 sm:pt-0 border-t border-purple-100 dark:border-purple-900/30 sm:border-none flex flex-col justify-center">
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-0.5">Package Price</p>
                  <p className="text-xl font-black text-gray-900 dark:text-white">
                    {request.service_package.price === 0 ? 'Free' : formatCurrency(request.service_package.price)}
                  </p>
                  {request.service_package.advance_percentage > 0 && (
                     <p className="text-[10px] font-bold text-orange-600 dark:text-orange-400 mt-1">
                       Requires {request.service_package.advance_percentage}% Advance
                     </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Additional Details */}
          {(request.requirements || request.deliverables || request.equipment_needed) && (
            <div className="border-t border-gray-200 dark:border-gray-800 pt-6 mb-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Additional Details</h4>
              
              {request.requirements && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Requirements</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{request.requirements}</p>
                </div>
              )}
              
              {request.deliverables && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Deliverables</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{request.deliverables}</p>
                </div>
              )}
              
              {request.equipment_needed && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Equipment Needed</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{request.equipment_needed}</p>
                </div>
              )}
            </div>
          )}

          {/* Portfolio References */}
          {request.portfolio_references && request.portfolio_references.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-800 pt-6 mb-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <LinkIcon className="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                Portfolio References
              </h4>
              <div className="space-y-2">
                {request.portfolio_references.map((link: string, index: number) => (
                  <a
                    key={index}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 hover:underline"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                    {link}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          {request.attachments && request.attachments.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-800 pt-6 mb-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <PaperClipIcon className="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                Attachments
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {request.attachments.map((file: any, index: number) => (
                  <a
                    key={index}
                    href={file.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-lg mr-3">
                      <PaperClipIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {file.file_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {(file.file_size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Preferred Dates */}
          {request.preferred_dates && request.preferred_dates.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-800 pt-5 mb-5">
              <h4 className="font-bold text-xs uppercase tracking-wider text-gray-900 dark:text-white mb-3 flex items-center">
                 <CalendarDaysIcon className="h-4 w-4 mr-1 text-teal-600 dark:text-teal-400" />
                 Preferred Dates
              </h4>
              <div className="flex flex-wrap gap-2">
                {request.preferred_dates.map((date: string, index: number) => (
                  <div key={index} className="flex items-center space-x-1.5 px-3 py-1.5 bg-teal-50 dark:bg-teal-900/20 border border-teal-100/60 dark:border-teal-900/30 rounded-lg text-teal-700 dark:text-teal-400 shadow-sm">
                    <CalendarDaysIcon className="h-4 w-4" />
                    <span className="text-xs font-bold">
                      {format(new Date(date), 'MMM d, yyyy')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expiration Warning */}
          {request.status === 'pending' && isExpiring() && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/50 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                  This request expires {formatDistanceToNow(new Date(request.expires_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          )}

          {/* Response Message if declined/accepted */}
          {request.response_message && (request.status === 'accepted' || request.status === 'declined') && (
            <div className="border-t border-gray-200 dark:border-gray-800 pt-6 mb-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                {isReceived ? 'Your Response' : 'Their Response'}
              </h4>
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">{request.response_message}</p>
                {request.responded_at && (
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                    Responded {formatDistanceToNow(new Date(request.responded_at), { addSuffix: true })}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Cancellation Details */}
          {request.status === 'cancelled' && request.cancellation_reason && (
            <div className="border-t border-gray-100 dark:border-gray-800 pt-5 mb-5">
              <div className="bg-gradient-to-br from-rose-50 to-white dark:from-rose-950/20 dark:to-gray-900 rounded-xl p-5 border border-rose-100 dark:border-rose-900/30 shadow-sm relative overflow-hidden">
                <div className="absolute -top-1 -right-1 p-3 opacity-[0.04] pointer-events-none rotate-12">
                   <ExclamationTriangleIcon className="w-20 h-20 text-rose-600" />
                </div>
                <h4 className="font-bold text-rose-800 dark:text-rose-400 mb-2 flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-1" />
                  Cancellation Notice
                </h4>
                <p className="font-black text-rose-900 dark:text-rose-200 text-sm mb-1">{request.cancellation_reason}</p>
                {request.cancellation_details && (
                  <p className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50/50 dark:bg-rose-900/10 p-2.5 rounded-lg border border-rose-100/50 dark:border-rose-900/10 whitespace-pre-wrap mt-2">{request.cancellation_details}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div 
          className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shrink-0"
          style={{ paddingBottom: 'calc(1.25rem + max(var(--safe-area-inset-bottom, 0px), env(safe-area-inset-bottom, 0px)))' }}
        >
          <div className="w-full grid grid-cols-2 sm:flex sm:justify-end gap-3">
            {/* Message User Button - Only visible if accepted or completed */}
            {(request.status === 'accepted' || request.status === 'completed') && (
              <Button
                variant="outline"
                onClick={(e) => handleMessageApplicant(request.sender.id != user.id ? request.receiver : request.sender)}
                className="flex items-center justify-center space-x-2 w-full sm:w-auto dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                disabled={
                   // Disable messaging only if 7 days have passed since completion
                   (request.status === 'completed' && request.updated_at && (
                     new Date().getTime() - new Date(request.updated_at).getTime() > 7 * 24 * 60 * 60 * 1000
                   ))
                }
              >
                <ChatBubbleLeftIcon className="h-4 w-4" />
                <span>Message</span>
              </Button>
            )}

            {/* Cancel Button */}
            {request.status === 'accepted' && onCancel && (
              <Button
                variant="outline"
                onClick={() => onCancel(request)}
                className="!text-rose-600 dark:!text-rose-400 !border-rose-200 dark:!border-rose-800/50 hover:!bg-rose-50 dark:hover:!bg-rose-900/20 hover:!border-rose-300 dark:hover:!border-rose-700 w-full sm:w-auto justify-center font-bold"
              >
                Cancel
              </Button>
            )}

            {/* Complete Button - occupy full-width col-span-2 on mobile if message and cancel are present */}
            {(request.status === 'accepted' || request.status === 'completed') && onComplete && (() => {
              const isSender = currentUser?.id === request.sender_id;
              const isReceiver = currentUser?.id === request.receiver_id;
              const hasMarkedComplete = isSender ? request.sender_marked_complete : isReceiver ? request.receiver_marked_complete : false;
              const hasOtherMarkedComplete = isSender ? request.receiver_marked_complete : isReceiver ? request.sender_marked_complete : false;
              const isFullyCompleted = request.status === 'completed';

              return (
                <Button
                  variant="primary"
                  onClick={() => {
                    if (hasOtherMarkedComplete) {
                      setShowCompleteConfirm(true);
                    } else {
                      onComplete && onComplete(request, false);
                    }
                  }}
                  disabled={hasMarkedComplete || isFullyCompleted}
                  className="col-span-2 sm:col-span-1 sm:w-auto justify-center bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400 border-none text-white font-bold shadow-lg shadow-teal-500/20"
                >
                  {isFullyCompleted || hasMarkedComplete ? 'Completed' : (hasOtherMarkedComplete ? 'Complete Collaboration' : 'Complete')}
                </Button>
              );
            })()}

            {/* Withdraw Button for Sent Pending Requests */}
            {!isReceived && request.status === 'pending' && onWithdraw && (
              <Button
                variant="outline"
                onClick={() => onWithdraw(request.id)}
                className="col-span-2 sm:w-auto justify-center !text-rose-600 dark:!text-rose-400 !border-rose-200 dark:!border-rose-800/50 hover:!bg-rose-50 dark:hover:!bg-rose-900/20 hover:!border-rose-300 dark:hover:!border-rose-700 font-bold"
              >
                Withdraw Request
              </Button>
            )}

            {/* Response Buttons for Received Pending Requests */}
            {isReceived && request.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleResponse('declined')}
                  loading={responding}
                  className="!text-rose-600 dark:!text-rose-400 !border-rose-200 dark:!border-rose-800/50 hover:!bg-rose-50 dark:hover:!bg-rose-900/20 hover:!border-rose-300 dark:hover:!border-rose-700 w-full sm:w-auto justify-center font-bold"
                >
                  Decline
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleResponse('accepted')}
                  loading={responding}
                  className="w-full sm:w-auto justify-center bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400 border-none text-white font-bold shadow-lg shadow-teal-500/20"
                >
                  Accept
                </Button>
              </>
            )}
          </div>
        </div>
      </motion.div>
      {showPackageDetails && request.service_package && (
        <ServicePackageDetailsModal
          isOpen={showPackageDetails}
          onClose={() => setShowPackageDetails(false)}
          packageData={request.service_package}
          isOwner={false}
          zIndex={10010}
        />
      )}
      {/* Confirmation Modal for Completing Collaboration */}
      <ConfirmationModal
        isOpen={showConfirmComplete}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={() => {
          onComplete && onComplete(request, true);
          setShowCompleteConfirm(false);
        }}
        title="Confirm Completion"
        message="Mark this collaboration as complete? This action cannot be undone. Once both parties complete, the chat will remain open for 7 days before becoming read-only."
        confirmText="Yes, Complete"
        cancelText="Cancel"
        type="danger"
        loading={false}
      />
    </div>,
    document.body
  );
};

export default RequestDetailModal;
